// src/core/me/provider/MeProvider.jsx
import React from 'react';
import { useLocation } from 'react-router-dom';
import { tokenStorage } from 'shared/api/storage';
import { meApi } from '../api/meApi';

const MeCtx = React.createContext(null);

function isPublicPath(pathname) {
  return (
    pathname.startsWith('/rsvp/') ||
    pathname === '/sign-in' ||
    pathname === '/sign-up' ||
    pathname === '/forgot-password' ||
    pathname === '/verify-email' ||
    pathname === '/login' ||
    pathname === '/register'
  );
}

export function MeProvider({ children }) {
  const loc = useLocation();

  const [me, setMe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const refresh = React.useCallback(async () => {
    // ✅ public sayfalarda me çağırma (RSVP burada)
    if (isPublicPath(loc.pathname)) {
      setLoading(false);
      setError(null);
      // me'yi burada zorla null'lamıyorum; istersen setMe(null) da diyebilirsin
      return null;
    }

    const tokens = tokenStorage.get();
    if (!tokens?.accessToken) {
      setMe(null);
      setError(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const dto = await meApi.getMe();
      setMe(dto);
      return dto;
    } catch (e) {
      setMe(null);

      // ❌ burada tokenStorage.clear() yapma
      // Çünkü global auth event tetikleniyor ve public sayfada bile redirect zinciri başlatıyor.

      setError(e?.message || 'Me fetch failed');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [loc.pathname]);

  // ✅ ilk mount + route değişince (pathname dependency yüzünden) refresh olur
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // ✅ login/logout sonrası sinyal
  React.useEffect(() => {
    const onAuthChanged = () => refresh();
    window.addEventListener('solis:auth:changed', onAuthChanged);
    return () => window.removeEventListener('solis:auth:changed', onAuthChanged);
  }, [refresh]);

  const value = React.useMemo(
    () => ({ me, loading, error, refresh }),
    [me, loading, error, refresh]
  );

  return <MeCtx.Provider value={value}>{children}</MeCtx.Provider>;
}

export function useMe() {
  const ctx = React.useContext(MeCtx);
  if (!ctx) throw new Error('useMe must be used inside <MeProvider />');
  return ctx;
}
